import React, { useState } from 'react';
import { apiCall } from '../config';
import './AuditFGInventory.css';

const AuditFGInventory = () => {
  const [auditData, setAuditData] = useState([]);
  const [qboInventoryData, setQboInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [latestInventoryDate, setLatestInventoryDate] = useState('');
  const [qboBalanceDate, setQboBalanceDate] = useState('');
  const [qboErrors, setQboErrors] = useState([]);

  const fetchAuditData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiCall('/api/audit-fg-inventory/data', {
        method: 'GET'
      });

      if (response.ok) {
        const data = await response.json();
        setAuditData(data.audit_data || []);
        setLatestInventoryDate(data.latest_inventory_date || '');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch audit data');
      }
    } catch (err) {
      setError('Network error occurred while fetching audit data');
    } finally {
      setLoading(false);
    }
  };

  const fetchQBOBalance = async () => {
    setLoading(true);
    setError('');
    setQboErrors([]);
    
    try {
      const response = await apiCall('/api/audit-fg-inventory/qbo-balance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}) // No credentials needed, backend will use stored ones
      });

      if (response.ok) {
        const data = await response.json();
        setQboInventoryData(data.qbo_inventory_data || []);
        setQboErrors(data.errors || []);
        setQboBalanceDate(data.balance_date || '');
        setLatestInventoryDate(data.latest_inventory_date || '');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch QBO balance');
      }
    } catch (err) {
      setError('Network error occurred while fetching QBO balance');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadData = async () => {
    try {
      // Load QBO data directly (skip legacy data)
      await fetchQBOBalance();
    } catch (error) {
      setError('Error loading audit data: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate flat table rows from QBO inventory data
  const generateTableRows = () => {
    const rows = [];
    let grandTotalPhysicalCount = 0;
    let grandTotalInventoryValue = 0;
    let grandTotalQboBalance = 0;
    
    qboInventoryData.forEach((account, accountIndex) => {
      // Add individual SKU rows for this account
      account.physical_inventory_items.forEach((item, itemIndex) => {
        rows.push({
          type: 'sku',
          accountId: account.account_id,
          accountName: account.account_name,
          igdSkuNumber: item.igd_sku_number,
          productName: item.igd_name,
          physicalCount: item.physical_inventory_count,
          costPerUnit: item.total_cost_per_unit,
          inventoryValue: item.inventory_value,
          qboBalance: null, // Blank for individual SKU rows
          variance: null,   // Blank for individual SKU rows
          isAccountGroupStart: itemIndex === 0, // First SKU in account group
          isAccountGroupEnd: false
        });
      });
      
      // Add subtotal row for this account
      const totalPhysicalCount = account.physical_inventory_items.reduce((sum, item) => sum + item.physical_inventory_count, 0);
      
      rows.push({
        type: 'subtotal',
        accountId: account.account_id,
        accountName: account.account_name,
        igdSkuNumber: `SUB-TOTAL ${account.account_name}`,
        productName: '',
        physicalCount: totalPhysicalCount,
        costPerUnit: null,
        inventoryValue: account.total_physical_value,
        qboBalance: account.qbo_balance,
        variance: account.variance,
        isAccountGroupStart: false,
        isAccountGroupEnd: true // Last row in account group
      });
      
      // Add spacing row (except after the last account)
      if (accountIndex < qboInventoryData.length - 1) {
        rows.push({
          type: 'spacing',
          accountId: null,
          accountName: null,
          igdSkuNumber: '',
          productName: '',
          physicalCount: null,
          costPerUnit: null,
          inventoryValue: null,
          qboBalance: null,
          variance: null,
          isAccountGroupStart: false,
          isAccountGroupEnd: false
        });
      }
      
      // Accumulate grand totals
      grandTotalPhysicalCount += totalPhysicalCount;
      grandTotalInventoryValue += account.total_physical_value;
      grandTotalQboBalance += account.qbo_balance;
    });
    
    // Add grand total row at the bottom
    const grandTotalVariance = grandTotalQboBalance - grandTotalInventoryValue;
    
    rows.push({
      type: 'grand-total',
      accountId: null,
      accountName: null,
      igdSkuNumber: 'GRAND TOTAL',
      productName: '',
      physicalCount: grandTotalPhysicalCount,
      costPerUnit: null,
      inventoryValue: grandTotalInventoryValue,
      qboBalance: grandTotalQboBalance,
      variance: grandTotalVariance,
      isAccountGroupStart: false,
      isAccountGroupEnd: false
    });
    
    return rows;
  };

  return (
    <div className="audit-fg-inventory">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Audit FG Inventory Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Compare physical inventory with QBO accounting records</li>
              <li>Track inventory variances and discrepancies</li>
              <li>Generate audit reports for financial reconciliation</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="audit-header">
          <h1>Audit FG Inventory</h1>
          <p>Compare physical inventory with QuickBooks Online accounting records</p>
        </div>

        <div className="audit-content">
        <div className="controls-section">
          <div className="load-button-container">
            <button 
              className="btn btn-primary load-audit-btn"
              onClick={handleLoadData}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load QBO Audit Data'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        {qboInventoryData.length > 0 && (
          <div className="audit-results">
            <h2>Audit Results</h2>
            
            {/* Data Timestamps */}
            <div className="data-timestamps">
              <div className="timestamps-container">
                {latestInventoryDate && (
                  <div className="inventory-date-info">
                    <p><strong>Inventory Read Date:</strong> {formatDate(latestInventoryDate)}</p>
                  </div>
                )}
                {qboBalanceDate && (
                  <div className="qbo-date-info">
                    <p><strong>QBO Data Retrieved:</strong> {formatDate(qboBalanceDate)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* QBO Errors Display */}
            {qboErrors.length > 0 && (
              <div className="qbo-errors">
                <h3>⚠️ QBO API Errors</h3>
                <div className="error-list">
                  {qboErrors.map((error, index) => (
                    <p key={index} className="error-item">• {error}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Flat Table View */}
            <div className="audit-table-section">
              <h3>Inventory Audit Table</h3>
              <div className="table-container">
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>IGD SKU Number</th>
                      <th>Product Name</th>
                      <th>Physical Count</th>
                      <th>Cost per Unit</th>
                      <th>Inventory Value</th>
                      <th>QBO Balance</th>
                      <th>Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generateTableRows().map((row, index) => (
                      <tr 
                        key={index} 
                        className={
                          (row.type === 'subtotal' ? 'subtotal-row' : 
                          row.type === 'grand-total' ? 'grand-total-row' :
                          row.type === 'spacing' ? 'spacing-row' : 'sku-row') +
                          (row.isAccountGroupStart ? ' account-group-start' : '') +
                          (row.isAccountGroupEnd ? ' account-group-end' : '')
                        }
                      >
                        <td className="sku-cell">{row.igdSkuNumber}</td>
                        <td className="product-name">{row.productName}</td>
                        <td className="inventory-count">{row.physicalCount !== null ? formatNumber(row.physicalCount) : ''}</td>
                        <td className="cost-per-unit">{row.costPerUnit !== null ? formatCurrency(row.costPerUnit) : ''}</td>
                        <td className="inventory-value">{row.inventoryValue !== null ? formatCurrency(row.inventoryValue) : ''}</td>
                        <td className="qbo-balance">{row.qboBalance !== null ? formatCurrency(row.qboBalance) : ''}</td>
                        <td className={`variance ${row.variance !== null ? (row.variance >= 0 ? 'positive' : 'negative') : ''}`}>
                          {row.variance !== null ? formatCurrency(row.variance) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default AuditFGInventory; 